// R56 — "COPIED" WAS FLASHED OVER A WRITE TWO SURFACES EXPLICITLY SWALLOWED, AND A THIRD NEVER
// WAITED FOR THE ANSWER AT ALL.
//
// THE HUNT is R47's — COUNT THE COPIES, and the count IS the finding. `navigator.clipboard` has FIVE
// call sites in App.tsx (measured 2026-08-01) and **THREE DIFFERENT BEHAVIOURS** between them:
//
//   * **TWO FLASHED OVER A SWALLOWED WRITE.** `copySavedVerse` (My Room's verse panel) and the verse
//     drawer's `copy` both wrote `navigator.clipboard?.writeText(text).catch(() => {})` and then set
//     the chip to "Copied" on the very next line, unconditionally. It is R54's defect one API over,
//     and the optional chaining makes it worse rather than safer: **on a browser with no Clipboard
//     API at all the whole expression short-circuits to `undefined`, nothing is written, nothing
//     throws, and the chip says Copied.** She pastes and gets whatever was there before.
//   * **ONE NEVER AWAITED.** Ask Lala's `copyMessage` wrapped a bare `navigator.clipboard?.writeText(text)`
//     in a `try`, which catches a SYNCHRONOUS throw and nothing else — a rejected write is an
//     unhandled rejection and the check icon flips anyway.
//   * **TWO WERE HONEST ABOUT THE WRITE AND SILENT ABOUT THE FAILURE** (the conjure drawer and the
//     angel card): `await` inside a `try`, the flash inside it, so the tick is real — and the `catch`
//     is empty, so a refused copy is a tap that does nothing at all. **AN ABSENT FLASH HAS NO ERROR
//     STATE** (R25's argument, applied to a control rather than an absent one), and this is not a
//     rare path: `navigator.clipboard` is undefined on plain HTTP and inside several in-app
//     browsers, and `writeText` rejects when the document is not focused.
//
// **THE FLASH IS THE ONLY THING THIS CONTROL EVER SAYS, SO IT HAS TO CARRY THE ANSWER.** There is no
// sentence to add on four of the five surfaces without inventing a status line each; the chip is
// already a two-state label at every one of them, so it becomes a THREE-state label derived from the
// answer — R47's shape exactly, where `saveReadingState` picks the chip and `saveReadingNote` picks
// the sentence.
//
// **THE FLASH LENGTH IS DECLARED ONCE HERE AND IT HAD ALREADY DRIFTED**: four sites used 1500ms and
// Ask Lala's used 1400, which is what a number typed five times does.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

// THREE ANSWERS, and `unavailable` is deliberately its own rather than folded into `refused`:
//   unavailable — this browser has no Clipboard API to ask. Nothing was attempted.
//   refused     — the API was there and said no (no user gesture, document not focused, permission).
//   ok          — the text is really on her clipboard.
// They are told apart because a house that ever wants to explain WHY needs the distinction to exist;
// today both take the same sentence, and that is a copy decision rather than a lost fact (R18).
export type CopyAnswer = { ok: true } | { ok: false; kind: "unavailable" } | { ok: false; kind: "refused" };

// How long the flash stays up. **ONE NUMBER**, because five hand-typed copies of it had already
// become two different numbers.
export const COPY_FLASH_MS = 1500;

// The clipboard is a PARAMETER (R46's locked-door shape) so a gate drives every answer without a
// browser — including the one that has no clipboard at all, which is the case the optional chaining
// was quietly turning into a success.
type Clip = { writeText: (text: string) => Promise<void> } | null | undefined;

export async function copyText(
  text: string,
  clipboard: Clip = (globalThis as { navigator?: { clipboard?: Clip } }).navigator?.clipboard,
): Promise<CopyAnswer> {
  if (!clipboard || typeof clipboard.writeText !== "function") return { ok: false, kind: "unavailable" };
  try {
    await clipboard.writeText(text);
    return { ok: true };
  } catch {
    return { ok: false, kind: "refused" };
  }
}

// THE CHIP, derived from the answer rather than from a boolean. **A BOOLEAN CANNOT SAY "IT DID NOT
// GO"** — that is ITEM 7's `self` argument one control down: two states cannot carry three facts, so
// the third one was rendered as the second for as long as these buttons have existed.
export type CopyState = "idle" | "copied" | "failed";
export function copyState(answer: CopyAnswer): CopyState {
  return answer.ok ? "copied" : "failed";
}

// The word on the control. Handed in by whoever holds the string catalog — this module imports
// nothing and cannot read it (`saved-books.ts`'s fallback-parameter shape).
export type CopyCopy = { copy: string; copied: string; failed: string };
export function copyLabel(state: CopyState, copy: CopyCopy): string {
  if (state === "copied") return copy.copied;
  if (state === "failed") return copy.failed;
  return copy.copy;
}

// The three rows, resolved ONCE rather than as fifteen `t()` calls across five surfaces — R24's
// argument, where eighteen call sites each typed the room name as a literal and a literal at a call
// site is held by nothing at all. `t` is a PARAMETER for the same reason `doFetch` is one (R46):
// this module imports nothing, so the catalog is handed in and a gate can drive it with its own.
export function copyWords(language: string, t: (language: string, key: string) => string): CopyCopy {
  return {
    copy: t(language, "common.copy"),
    copied: t(language, "common.copied"),
    failed: t(language, "common.copyFailed"),
  };
}
